import React, { useCallback, useEffect, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchCommunityResources } from '@/store/slices/residents/homeSlice';
import { fetchWifiInfo } from '@/store/slices/residents/wifiSlice';
import { CommunityResource } from '@/types/residents/communityResource';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { RESOURCES_COLORS } from '@/constants/residents/mobile/resourcesColors';
import { homeStrings } from '@/constants/residents/strings';

// Layout components
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { MobileScreenLayout } from '@/components/residents/mobile/MobileScreenLayout';

// Resource components
import { MobileWifiInfoCard } from '@/components/residents/resources/mobile/MobileWifiInfoCard';
import { MobileWifiSupportModal } from '@/components/residents/resources/mobile/MobileWifiSupportModal';
import { MobileResourceCard } from '@/components/residents/resources/mobile/MobileResourceCard';

type NavigationProp = StackNavigationProp<RootStackParamList>;

/**
 * Mobile Resources Screen
 * Matches new Figma design with gradient frame, dark theme, WiFi card, and resource list
 */
export const MobileResourcesScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const dispatch = useAppDispatch();

  // Redux state
  const { resources, isLoading: resourcesLoading } = useAppSelector((state) => state.home);
  const { wifiInfo, isLoading: wifiLoading } = useAppSelector((state) => state.wifi);
  const { user } = useAppSelector((state) => state.auth);
  const leaseId = user?.leaseId;
  const propertyId = user?.propertyId;
  const propertyName = user?.property?.marketingName;
  const unitNumber = user?.unitNumber;
  const property = user?.property;

  // Check if handbook URL exists
  const handbookUrl = property?.faqUrl || property?.websites?.handbook;
  const hasHandbookUrl = !!handbookUrl;

  // Local state
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    if (propertyId) {
      dispatch(fetchCommunityResources(propertyId));
    }
    if (leaseId) {
      dispatch(fetchWifiInfo(leaseId));
    }
  }, [dispatch, propertyId, leaseId]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const promises: Promise<unknown>[] = [];
      if (propertyId) {
        promises.push(dispatch(fetchCommunityResources(propertyId)));
      }
      if (leaseId) {
        promises.push(dispatch(fetchWifiInfo(leaseId)));
      }
      await Promise.all(promises);
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, propertyId, leaseId]);

  // Handle "Having issues?" press
  const handleHavingIssuesPress = useCallback(() => {
    setShowSupportModal(true);
  }, []);

  // Handle support modal close
  const handleSupportModalClose = useCallback(() => {
    setShowSupportModal(false);
  }, []);

  // Handle resource card press - navigate to detail screen
  const handleResourcePress = useCallback((resource?: CommunityResource) => {
    if (resource) {
      navigation.navigate('MobileResourceDetail', { resource });
    }
  }, [navigation]);

  // Handle Contact Office press
  const handleContactOfficePress = useCallback(() => {
    navigation.navigate('MobileContactOffice');
  }, [navigation]);

  // Handle Resident Handbook press
  const handleResidentHandbookPress = useCallback(() => {
    if (handbookUrl) {
      navigation.navigate('WebView', {
        url: handbookUrl,
        title: homeStrings.faqs.title,
      });
    }
  }, [navigation, handbookUrl]);

  return (
    <BaseResidentMobileScreen
      screenName="Resources"
      darkBackground={true}
    >
      <MobileScreenLayout
        backgroundColor={RESOURCES_COLORS.background}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        refreshTintColor={RESOURCES_COLORS.textPrimary}
        lightStatusBar={true}
      >
        {/* REVISIT FEATURE: WiFi Info Card */}
        {/* WiFi Info Card - Show if wifi info is available */}
        {/* {wifiInfo && (
          <MobileWifiInfoCard
            wifiInfo={wifiInfo}
            propertyName={propertyName}
            unitNumber={unitNumber}
            onHavingIssuesPress={handleHavingIssuesPress}
          />
        )} */}

        {/* Contact Office Card */}
        <MobileResourceCard
          title="Contact the office"
          description="Get in touch with your property management"
          iconName="chatbubble-outline"
          onPress={handleContactOfficePress}
        />

        {/* Resident Handbook Card - Show if handbook URL exists */}
        {hasHandbookUrl && (
          <MobileResourceCard
            title="Resident handbook"
            description="View FAQs and community guidelines"
            iconName="book-outline"
            onPress={handleResidentHandbookPress}
          />
        )}

        {/* Resource Cards */}
        {resources.map((resource) => (
          <MobileResourceCard
            key={resource.id}
            resource={resource}
            onPress={handleResourcePress}
          />
        ))}
      </MobileScreenLayout>

      {/* WiFi Support Modal */}
      <MobileWifiSupportModal
        visible={showSupportModal}
        wifiInfo={wifiInfo}
        onClose={handleSupportModalClose}
      />
    </BaseResidentMobileScreen>
  );
};
